import zod from 'zod';
import { stringToBoolean, stringToListOfEnum, stringToListOfNumber, zodLimit, zodPage } from './helpers';

export const cameraTypes = [
    'ptz',
    'panoramic',
    'lpr',
    'pinned',
] as const;

export type CameraType = typeof cameraTypes[number];

export const algorithmFilter = [
    'hasFacial',
    'hasLpr',
    'hasAreaInvasion',
] as const;

export type AlgorithmFilter = typeof algorithmFilter[number];

export const integrationTypes = [
    'type_1',
    'type_2',
    'type_3',
] as const;

export type IntegrationType = typeof integrationTypes[number];

export const storageTypes = [
    'cloud',
    'card',
] as const;

export type StorageTypeFilter = typeof storageTypes[number];


const CamerasListSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    online: stringToBoolean,
    offline: stringToBoolean.optional(),
    tagsIds: stringToListOfNumber.optional(),
    type: zod.enum(cameraTypes).optional(),
    integrationType: stringToListOfEnum(zod.enum(integrationTypes)).optional(),
    // TODO: remove when migrated to thirdPartyApprovedCompanies
    origin: zod.string().optional(),
    polygonType: zod.string().optional(),
    polygon: zod.coerce.number().optional(),
    // TODO: Add when migrated to thirdPartyApprovedCompanies
    // imageProvider: zod.string().optional(),
    // cloudProvider: zod.string().optional()
});

const MosaicSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

const CameraTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

const DownloadTagsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

const AlertGroupsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    cameraTagIds: stringToListOfNumber.optional()
});

const CamerasReportsSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
});

const VideoAnalysisSchema = zod.object({
    page: zodPage,
    limit: zodLimit(),
    textFilter: zod.string().optional(),
    status: zod.string().optional(),
    startDate: zod.coerce.number().optional(),
    endDate: zod.coerce.number().optional(),
    requesterUserId: zod.coerce.number().optional(),
});

const CameraDetailsSchema = zod.object({
    id: zod.string(),
});

const CamerasMapSchema = zod.object({});

const MosaicDetailSchema = zod.object({
    id: zod.coerce.number(),
});

const VideoAnalysisDetailsSchema = zod.object({
    id: zod.coerce.number(),
});

export const CamerasSchemas = {
    CamerasList: CamerasListSchema,
    CameraPlayer: zod.object({
        id: zod.string(),
    }),
    Mosaic: MosaicSchema,
    CameraTags: CameraTagsSchema,
    DownloadTags: DownloadTagsSchema,
    Downloads: zod.object({
        page: zodPage,
        limit: zodLimit(),
        textFilter: zod.string().optional(),
        tagsIds: stringToListOfNumber.optional(),
        userId: zod.coerce.number().optional(),
        cameraId: zod.string().optional(),
        actingBodyId: zod.coerce.number().optional(),
        actingBodyUnitIds: stringToListOfNumber.optional(),
    }),
    AlertGroups: AlertGroupsSchema,
    UpsertAlertGroupModal: zod.object({
        id: zod.coerce.number().optional(),
    }).optional(),
    CamerasReports: CamerasReportsSchema,
    VideoAnalysis: VideoAnalysisSchema,
    CameraDetails: CameraDetailsSchema,
    CamerasMap: CamerasMapSchema,
    CameraMapPlayer: zod.object({
        id: zod.string(),
    }),
    MosaicDetail: MosaicDetailSchema,
    VideoAnalysisDetails: VideoAnalysisDetailsSchema,
} as const;
